import React, { useEffect, useState } from 'react';
import {
    IonBadge,
    IonContent,
    IonIcon,
    IonSpinner,
    useIonToast,
    useIonViewDidEnter
} from '@ionic/react';
import { Link, useLocation } from 'react-router-dom';
import {
    searchOutline,
    printOutline,
    closeOutline,
    checkmarkCircle,
    airplaneOutline,
    boatOutline,
    timeOutline
} from 'ionicons/icons';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer';
import { onScroll } from '../../services/sticky-menu.service';
import { useAppContext } from '../../services/app-context.service';
import { appSubDir } from '../../configs/app';
import { fetchShippingBooking, fetchUserShippingBookings } from '../../services/shipping-booking.service';
import { ShippingBooking } from '../../types/shipping-booking.type';
import { printShippingChallan } from '../../services/print.service';
import Pagination from '../../components/Pagination';
import './MyShippingBookings.scss';

const BDT = new Intl.NumberFormat('en-IN');

const statusStages = [
    { key: 'Pending', label: 'Booking Placed' },
    { key: 'Received in China Warehouse', label: 'China Warehouse' },
    { key: 'Shipped To BD', label: 'Shipped To BD' },
    { key: 'BD Warehouse', label: 'BD Warehouse' },
    { key: 'Delivered', label: 'Delivered' }
];

const MyShippingBookings: React.FC = () => {
    const { user, settings } = useAppContext();
    const [presentToast] = useIonToast();
    const location = useLocation();

    const [loading, setLoading] = useState(false);
    const [bookings, setBookings] = useState<ShippingBooking[]>([]);
    const [activeBooking, setActiveBooking] = useState<ShippingBooking | null>(null);
    const [trackingQuery, setTrackingQuery] = useState('');
    const [page, setPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [searching, setSearching] = useState(false);

    useIonViewDidEnter(() => {
        const tabBar = document.querySelector('ion-tab-bar');
        if (tabBar !== null) {
            tabBar.style.display = 'none';
        }
    });

    const loadUserBookings = async (pageNo = 1) => {
        if (!user.token) return;
        setLoading(true);
        try {
            const res: any = await fetchUserShippingBookings(pageNo);
            const responseData = res?.data;
            if (responseData && responseData.data) {
                setBookings(responseData.data);
                setLastPage(responseData.last_page ?? 1);
            }
        } catch (err) {
            console.error('Failed to load user bookings:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadUserBookings(page);
    }, [page, user.token]);

    // Check if query string has a track parameter (e.g. ?id=SB-...)
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const trackId = params.get('track') || params.get('id');
        if (trackId) {
            setTrackingQuery(trackId);
            handleTrackSubmit(trackId);
        }
    }, [location.search]);

    const handleTrackSubmit = async (queryToSearch = trackingQuery) => {
        const q = queryToSearch.trim();
        if (!q) {
            presentToast({ message: 'Please enter a Booking ID or Tracking Number', duration: 2500, color: 'warning' });
            return;
        }

        setSearching(true);
        try {
            const booking = await fetchShippingBooking(q);
            setActiveBooking(booking);
            // Scroll to tracker
            setTimeout(() => {
                const el = document.getElementById('live-status-tracker');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
            }, 100);
        } catch (err: any) {
            console.error('Tracking search error:', err);
            presentToast({
                message: err.response?.data?.message || 'No shipping booking found matching this ID or Tracking Number.',
                duration: 3500,
                color: 'danger'
            });
        } finally {
            setSearching(false);
        }
    };

    const getStageIndex = (status: string) => {
        if (status === 'Cancelled') return -1;
        const index = statusStages.findIndex(s => s.key === status);
        return index !== -1 ? index : 0;
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'Pending':
                return 'warning';
            case 'Received in China Warehouse':
                return 'primary';
            case 'Shipped To BD':
                return 'secondary';
            case 'BD Warehouse':
                return 'tertiary';
            case 'Delivered':
                return 'success';
            case 'Cancelled':
                return 'danger';
            default:
                return 'medium';
        }
    };

    const currentStageIndex = activeBooking ? getStageIndex(activeBooking.status) : 0;

    return (
        <IonContent fullscreen scrollEvents={true} onIonScroll={onScroll}>
            <Header />

            <div className="my-shipping-bookings-page">
                <div className="bookings-container">
                    {/* Top Bar */}
                    <div className="bookings-top-bar">
                        <div>
                            <h1 className="page-title">Shipping Booking Status</h1>
                            <small style={{ color: '#64748b' }}>
                                View your parcel movement timeline, tracking numbers, and Challans
                            </small>
                        </div>
                        <Link to={appSubDir + "create-booking"} className="btn-new-booking">
                            + Book Shipping Service
                        </Link>
                    </div>

                    {/* Quick Track Input Bar */}
                    <div className="quick-track-card">
                        <h3>Quick Track Any Booking</h3>
                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                handleTrackSubmit();
                            }}
                            className="track-input-group"
                        >
                            <input
                                type="text"
                                placeholder="Enter Booking ID (e.g. SB-261029-...) or China Tracking No"
                                value={trackingQuery}
                                onChange={(e) => setTrackingQuery(e.target.value)}
                            />
                            <button type="submit" disabled={searching}>
                                {searching ? <IonSpinner name="dots" /> : (
                                    <>
                                        <IonIcon icon={searchOutline} /> Track Status
                                    </>
                                )}
                            </button>
                        </form>
                    </div>

                    {/* Active Live Status Tracker & Timeline */}
                    {activeBooking && (
                        <div className="status-tracker-box" id="live-status-tracker">
                            <div className="tracker-header">
                                <div>
                                    <h2>Booking #{activeBooking.booking_id}</h2>
                                    <small style={{ color: '#64748b' }}>
                                        Booked on {new Date(activeBooking.created_at || Date.now()).toLocaleDateString()}
                                    </small>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <IonBadge color={getStatusColor(activeBooking.status)} style={{ fontSize: '0.95rem', padding: '6px 12px' }}>
                                        {activeBooking.status}
                                    </IonBadge>
                                    <button
                                        type="button"
                                        className="close-tracker-btn"
                                        onClick={() => setActiveBooking(null)}
                                        aria-label="Close Tracker"
                                    >
                                        &times;
                                    </button>
                                </div>
                            </div>

                            {/* Timeline Stepper */}
                            {activeBooking.status === 'Cancelled' ? (
                                <div style={{ background: '#fef2f2', border: '1px solid #fecdd3', borderRadius: 8, padding: 16, color: '#b91c1c', marginBottom: 20, textAlign: 'center' }}>
                                    <strong>This shipping booking was cancelled.</strong>
                                </div>
                            ) : (
                                <div className="timeline-steps">
                                    {statusStages.map((stage, idx) => {
                                        const isCompleted = idx < currentStageIndex;
                                        const isActive = idx === currentStageIndex;
                                        return (
                                            <div
                                                key={stage.key}
                                                className={`timeline-step ${isCompleted ? 'completed' : ''} ${isActive ? 'active' : ''}`}
                                            >
                                                <div className="step-circle">
                                                    {isCompleted ? '✓' : idx + 1}
                                                </div>
                                                <div className="step-label">{stage.label}</div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Booking Info Grid */}
                            <div className="booking-details-grid">
                                <div className="detail-item">
                                    <strong>Shipping Mark</strong>
                                    <span>{activeBooking.shipping_mark}</span>
                                </div>
                                <div className="detail-item">
                                    <strong>Tracking Number</strong>
                                    <span style={{ fontFamily: 'monospace' }}>{activeBooking.tracking_no}</span>
                                </div>
                                <div className="detail-item">
                                    <strong>Shipment Method</strong>
                                    <span>
                                        <IonIcon
                                            icon={activeBooking.method?.toLowerCase().includes('sea') ? boatOutline : airplaneOutline}
                                            style={{ verticalAlign: 'middle', marginRight: 4 }}
                                        />
                                        {activeBooking.method}
                                    </span>
                                </div>
                                <div className="detail-item">
                                    <strong>Cartons &amp; Weight</strong>
                                    <span>{activeBooking.carton_count} ctn / {activeBooking.weight} kg</span>
                                </div>
                                <div className="detail-item">
                                    <strong>Route</strong>
                                    <span>{activeBooking.shipping_route || 'Guangzhou'}</span>
                                </div>
                                <div className="detail-item">
                                    <strong>Total Bill</strong>
                                    <span>{BDT.format(activeBooking.total_cost)}/=</span>
                                </div>
                                <div className="detail-item">
                                    <strong>Paid / Due</strong>
                                    <span style={{ color: activeBooking.due > 0 ? '#e11d48' : '#059669' }}>
                                        Paid: {BDT.format(activeBooking.paid)}/= | Due: {BDT.format(activeBooking.due)}/=
                                    </span>
                                </div>
                            </div>

                            {/* Items List */}
                            {activeBooking.items && activeBooking.items.length > 0 && (
                                <div style={{ marginBottom: 16 }}>
                                    <strong style={{ fontSize: '0.9rem', color: '#334155', display: 'block', marginBottom: 8 }}>
                                        Items in Cartons ({activeBooking.items.length}):
                                    </strong>
                                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                        {activeBooking.items.map((it, i) => (
                                            <span
                                                key={i}
                                                style={{ background: '#f1f5f9', border: '1px solid #cbd5e1', padding: '4px 10px', borderRadius: 6, fontSize: '0.85rem' }}
                                            >
                                                <b>{it.item_name}</b> (Qty: {it.quantity})
                                                {it.is_special_category && <span style={{ color: '#d9235e', marginLeft: 4 }}>[Special]</span>}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Actions */}
                            <div className="tracker-actions">
                                <button
                                    type="button"
                                    className="btn-print"
                                    onClick={() => printShippingChallan(activeBooking, settings)}
                                >
                                    <IonIcon icon={printOutline} /> Print Challan / Receipt
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Customer's Bookings List */}
                    {loading && (
                        <div style={{ textAlign: 'center', padding: '50px' }}>
                            <IonSpinner />
                        </div>
                    )}

                    {!loading && user.token && bookings.length === 0 && (
                        <div className="empty-bookings-box">
                            <h3>No Shipping Bookings Yet</h3>
                            <p>You haven't placed any freight forwarding bookings yet.</p>
                            <Link to={appSubDir + "create-booking"} className="btn-new-booking">
                                Create Your First Booking
                            </Link>
                        </div>
                    )}

                    {!loading && bookings.length > 0 && (
                        <div className="bookings-table-card">
                            {/* Desktop Table View */}
                            <div className="desktop-only">
                                <table className="bookings-table">
                                    <thead>
                                        <tr>
                                            <th>Booking ID</th>
                                            <th>Date</th>
                                            <th>Shipping Mark</th>
                                            <th>Tracking No</th>
                                            <th>Method</th>
                                            <th>Cartons / Weight</th>
                                            <th>Status</th>
                                            <th>Total / Due</th>
                                            <th style={{ textAlign: 'right' }}>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {bookings.map((item) => (
                                            <tr key={item.id}>
                                                <td>
                                                    <strong>#{item.booking_id}</strong>
                                                </td>
                                                <td>
                                                    {new Date(item.created_at || Date.now()).toLocaleDateString()}
                                                </td>
                                                <td>
                                                    <span style={{ fontWeight: 600, color: '#0a6670' }}>{item.shipping_mark}</span>
                                                </td>
                                                <td>
                                                    <code style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: 4 }}>
                                                        {item.tracking_no}
                                                    </code>
                                                </td>
                                                <td>{item.method}</td>
                                                <td>{item.carton_count} ctn / {item.weight} kg</td>
                                                <td>
                                                    <IonBadge color={getStatusColor(item.status)}>
                                                        {item.status}
                                                    </IonBadge>
                                                </td>
                                                <td>
                                                    <div>{BDT.format(item.total_cost)}/=</div>
                                                    {item.due > 0 && (
                                                        <small style={{ color: '#e11d48' }}>Due: {BDT.format(item.due)}/=</small>
                                                    )}
                                                </td>
                                                <td className="action-cell">
                                                    <button
                                                        type="button"
                                                        className="btn-view-status"
                                                        onClick={() => {
                                                            setActiveBooking(item);
                                                            window.scrollTo({ top: 120, behavior: 'smooth' });
                                                        }}
                                                    >
                                                        Track Status
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn-row-print"
                                                        onClick={() => printShippingChallan(item, settings)}
                                                        title="Print Challan"
                                                    >
                                                        <IonIcon icon={printOutline} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile Cards View */}
                            <div className="mobile-only">
                                {bookings.map((item) => (
                                    <div className="mobile-booking-card" key={item.id}>
                                        <div className="mobile-card-header">
                                            <strong>#{item.booking_id}</strong>
                                            <IonBadge color={getStatusColor(item.status)}>
                                                {item.status}
                                            </IonBadge>
                                        </div>
                                        <div className="mobile-card-row">
                                            <span>Date:</span>
                                            <strong>{new Date(item.created_at || Date.now()).toLocaleDateString()}</strong>
                                        </div>
                                        <div className="mobile-card-row">
                                            <span>Tracking No:</span>
                                            <code style={{ background: '#f1f5f9', padding: '1px 5px', borderRadius: 3 }}>
                                                {item.tracking_no}
                                            </code>
                                        </div>
                                        <div className="mobile-card-row">
                                            <span>Shipping Mark:</span>
                                            <strong>{item.shipping_mark}</strong>
                                        </div>
                                        <div className="mobile-card-row">
                                            <span>Method &amp; Cartons:</span>
                                            <strong>{item.method} ({item.carton_count} ctn, {item.weight} kg)</strong>
                                        </div>
                                        <div className="mobile-card-row">
                                            <span>Total / Due:</span>
                                            <strong>
                                                {BDT.format(item.total_cost)}/=
                                                {item.due > 0 && <span style={{ color: '#e11d48', marginLeft: 6 }}>Due: {BDT.format(item.due)}/=</span>}
                                            </strong>
                                        </div>
                                        <div className="mobile-card-actions">
                                            <button
                                                type="button"
                                                className="btn-view-status"
                                                onClick={() => {
                                                    setActiveBooking(item);
                                                    window.scrollTo({ top: 120, behavior: 'smooth' });
                                                }}
                                            >
                                                Live Status &amp; Timeline
                                            </button>
                                            <button
                                                type="button"
                                                className="btn-row-print"
                                                onClick={() => printShippingChallan(item, settings)}
                                            >
                                                <IonIcon icon={printOutline} /> Print
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {lastPage > 1 && (
                                <Pagination
                                    active={page}
                                    disabled={loading}
                                    buttonCount={lastPage}
                                    onClick={(p: number) => setPage(p + 1)}
                                />
                            )}
                        </div>
                    )}
                </div>
            </div>

            <Footer />
        </IonContent>
    );
};

export default MyShippingBookings;

